<template>
  <modal
    :visible="modalVisible"
    class="modal-delivery"
    @on-close="onCloseModal"
  >
    <div class="delivery-selector">
      <div class="delivery-selector__title">
        <span v-html="title" />
        <span class="delivery-selector__city" @click.stop="changeCity">
          {{ USER_CITY.name }}
        </span>
      </div>

      <div class="delivery-selector__body">
        <delivery-courier-tab
          v-if="viewType === viewTypes.courier.value"
          :products="products"
          :products-count="productsCount"
          :shipping-channels="shippingChannels"
          :current-city="USER_CITY"
          @change-view="viewType = $event"
          @close="closeModal"
        >
          <ui-segmented
            v-model="viewType"
            :labels="Object.values(viewTypes)"
            class="delivery-selector__view-type"
          />

          <checkout-delivery-widget
            :place="WIDGET_PLACES.courier"
            class="delivery-selector__delivery-widget"
          />
        </delivery-courier-tab>

        <delivery-pickup-tab
          v-if="viewType === viewTypes.pickup.value"
          :products="products"
          :point-id="pointId"
          :current-city="USER_CITY"
          @close="closeModal"
        >
          <ui-segmented
            v-model="viewType"
            :labels="Object.values(viewTypes)"
            class="delivery-selector__view-type"
          />

          <checkout-delivery-widget
            :place="WIDGET_PLACES.pickup"
            class="delivery-selector__delivery-widget"
          />
        </delivery-pickup-tab>
      </div>
    </div>
  </modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { TextBuilder } from '@/utils/text-builder';
import { pluralize } from '@/utils/string-utils';
import { getLineIds } from '@/components/checkout/delivery-selector/utils';
import { WIDGET_PLACES } from '@/components/checkout/checkout-delivery-widget/constants';

/* Components */
import DeliveryPickupTab from './delivery-pickup-tab.vue';
import DeliveryCourierTab from './delivery-courier-tab.vue';
import CheckoutDeliveryWidget from '@/components/checkout/checkout-delivery-widget/checkout-delivery-widget.vue';

import IconPickup from '@icons/point.svg';
import IconDelivery from '@icons/box.svg';

export const VIEW_TYPES = {
  courier: {
    value: 'courier',
    icon: IconDelivery.id,
    label: 'Курьером',
  },
  pickup: {
    value: 'pickup',
    icon: IconPickup.id,
    label: 'Самовывозом',
  },
};

export default {
  name: 'delivery-selector',

  components: {
    CheckoutDeliveryWidget,
    DeliveryCourierTab,
    DeliveryPickupTab,
  },

  icons: {
    IconPickup,
    IconDelivery,
  },

  data: () => ({
    modalVisible: false,
    viewType: VIEW_TYPES.pickup.value,
    products: [],
    shippingGroup: {},
    pointId: '',
  }),

  computed: {
    ...mapGetters({
      USER_CITY: 'user/city',
      PRODUCTS: 'cart/products',
      SHIPPING_CHANNELS: 'cart/shippingChannels',
      DELIVERY_INFO: 'cart/deliveryInfo',
      SHIPPING_GROUPS: 'cart/shippingGroups',
    }),

    WIDGET_PLACES: () => WIDGET_PLACES,

    viewTypes: () => VIEW_TYPES,

    productsCount() {
      const products = this.shippingGroup?.products || (
        this.viewType === VIEW_TYPES.courier.value
          ? this.deliveryShippingGroup?.products
          : this.products
      );

      return {
        count: getLineIds(products).length,
        total: getLineIds(this.PRODUCTS).length,
      };
    },

    title() {
      const productCount = getLineIds(this.products).length;
      const textBuilder = new TextBuilder('Выбор способа получения ');
      textBuilder.addStyledText(
        `для ${pluralize(productCount, ['товара', 'товаров', 'товаров'])} `,
        { 'white-space': 'nowrap' },
        !!productCount
      );
      textBuilder.addText('в городе');
      return textBuilder.toString();
    },

    shippingChannels() {
      return this.shippingGroup?.shippingChannels?.length
        ? this.shippingGroup.shippingChannels
        : this.SHIPPING_CHANNELS;
    },

    deliveryShippingGroup() {
      return this.SHIPPING_GROUPS.find(({ type }) => type === 'delivery');
    },

    isDeliveryAvailable() {
      return this.shippingChannels.find(({ type, available }) => type === 'delivery' && available);
    }
  },

  mounted() {
    this.$f.$on('allocate-pickup', this.onOpen);
  },

  methods: {
    ...mapActions({
      RESET_SHIPPING_GROUP: 'cart/resetShippingGroup',
      SET_SHIPPING_GROUP: 'cart/setShippingGroup',
    }),

    onOpen({ products = [], shippingGroup, type, pointId = '' } = {}) {
      if (!products.length) return;
      if (type) this.viewType = type;
      this.products = products;
      this.shippingGroup = shippingGroup;
      this.pointId = pointId;
      this.modalVisible = true;
    },

    async changeCity() {
      try {
        const city = await this.$city.changeCity();
        const isNeededReloadPage = this.$city.checkConfirmation(city);
        this.$city.requestConfirmation({ city, reloadPage: isNeededReloadPage });
        if (isNeededReloadPage) {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        } else {
          if (this.viewType !== VIEW_TYPES.courier.value) return;
          this.$f.$once('cart-city-changed', () => {
            if (!this.isDeliveryAvailable) return;
            this.SET_SHIPPING_GROUP({
              type: 'delivery',
              lineIds: getLineIds(this.products),
            });
          });
        }
      } catch (e) {
        e && console.error(e);
      }
    },

    closeModal() {
      this.modalVisible = false;
      this.viewType = VIEW_TYPES.pickup.value;
      this.$f.$emit('select-pickup');
    },

    async onCloseModal() {
      const { cityId, streetName, house } = this.DELIVERY_INFO?.address || {};
      if (this.deliveryShippingGroup && !(cityId && streetName && house)) {
        // todo временное решение, пока не будет удален компонент checkout-delivery
        this.RESET_SHIPPING_GROUP(this.products);
      }
      this.closeModal();
    },
  },
};
</script>

<style lang="scss">
.modal-delivery {
  .modal-content {
    width: 100%;
    height: 100%;
    max-height: 100%;
    padding: 44px 16px 0;
    border-radius: 0;

    & > .close-button {
      z-index: 5;
      padding: 12px;
      color: $gray_lu;

      .sprite-icon {
        margin: 0;
      }
    }
  }
}

.delivery-selector {
  display: flex;
  flex-direction: column;
  height: 100%;

  &__body {
    flex-grow: 1;
    margin-top: 16px;
  }

  &__title {
    margin-bottom: 12px;
    font-family: VAGRounded, sans-serif;
    font-size: 20px;
    line-height: 28px;
    font-weight: normal;
  }

  &__city {
    color: $blue;
    white-space: nowrap;
    cursor: pointer;
  }

  &__view-type {
    margin-bottom: 16px;
  }

  &__delivery-widget {
    margin-bottom: 16px;
  }
}

@include breakpoint("tablet") {
  .modal-delivery {
    .modal-content {
      padding: 56px 32px 0;
    }
  }

  .delivery-selector {
    &__title {
      margin-bottom: 24px;
    }
  }
}

@include breakpoint("desktop") {
  .modal-delivery {
    .modal-content {
      max-width: 1320px;
      height: 705px;
      padding: 20px 0 0 32px;

      & > .close-button {
        width: 44px;
        height: 44px;
      }
    }
  }

  .delivery-selector {
    height: 100%;
    display: flex;
    flex-direction: column;

    &__title {
      margin-bottom: 7px;
      font-size: 32px;
      line-height: 48px;
    }

    &__body {
      display: flex;
      justify-content: center;
      width: 100%;
      height: 100%;
      margin-top: 0;
      overflow: hidden;
    }

    &__view-type {
      margin-bottom: 12px;

      .ui-segmented__tab {
        width: 100%;
      }
    }
  }
}
</style>
